// src/components/common/OpenInExternalBrowser.jsx
// Shown only inside the Instagram / Facebook / Messenger in-app browser.
// Android: auto-redirects to the system browser. iOS: shows instructions to do it manually.
import React, { useEffect, useState } from 'react';
import { ExternalLink, Copy, Check, MoreHorizontal } from 'lucide-react';
import {
  isInAppBrowser, isIOS, escapeInAppBrowser,
} from '../../utils/inAppBrowser';
import { safeSessionStorage } from '../../utils/safeStorage';

const REDIRECT_FLAG = 'mintlink_ext_redirect_tried';

export default function OpenInExternalBrowser() {
  const [show, setShow]       = useState(false);
  const [copied, setCopied]   = useState(false);
  const url = typeof window !== 'undefined' ? window.location.href : '';

  useEffect(() => {
    // Android: stay inside the in-app browser (testing whether that causes issues).
    // iOS: keep auto-escaping — Chrome deep link, or the manual overlay as fallback.
    if (!isInAppBrowser() || !isIOS()) return;

    // Auto-attempt the escape once per session on BOTH platforms:
    //   Android → intent:// (system browser)   iOS → googlechromes:// (Chrome)
    // If it succeeds the page navigates away; if not (e.g. Chrome not installed on iOS)
    // we reveal the manual overlay as a fallback.
    const alreadyTried = safeSessionStorage.getItem(REDIRECT_FLAG);
    if (!alreadyTried) {
      safeSessionStorage.setItem(REDIRECT_FLAG, '1');
      escapeInAppBrowser(url);
      const t = setTimeout(() => setShow(true), 1500);
      return () => clearTimeout(t);
    }
    setShow(true);
  }, [url]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Fallback for older WebViews
      const el = document.createElement('textarea');
      el.value = url;
      document.body.appendChild(el);
      el.select();
      try { document.execCommand('copy'); } catch { /* ignore */ }
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!show) return null;

  const ios = isIOS();

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl p-6 text-center animate-[slideUp_0.25s_ease-out]">
        <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center bg-[#32764A]/10">
          <ExternalLink className="w-7 h-7 text-[#32764A]" />
        </div>

        <h2 className="text-lg font-extrabold text-gray-900">Open in {ios ? 'Chrome' : 'your browser'}</h2>
        <p className="text-sm text-gray-500 mt-1.5 leading-relaxed">
          You're viewing this inside Instagram's browser, where login &amp; payments can break.
          Tap below to continue in a real browser.
        </p>

        {/* One-tap button — Android: system browser · iOS: Chrome */}
        <button
          onClick={() => escapeInAppBrowser(url)}
          className="mt-5 w-full py-3 rounded-xl bg-[#32764A] text-white text-sm font-bold hover:bg-[#115435] transition inline-flex items-center justify-center gap-2"
        >
          <ExternalLink className="w-4 h-4" />Open in {ios ? 'Chrome' : 'browser'}
        </button>

        {/* iOS fallback for users without Chrome installed */}
        {ios && (
          <div className="mt-4 text-left bg-gray-50 rounded-2xl p-4">
            <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-2">No Chrome? Open in Safari</p>
            <div className="space-y-2.5">
              <Step n={1}>
                Tap the menu icon
                <span className="inline-flex items-center gap-1 mx-1 px-1.5 py-0.5 rounded-md bg-white border border-gray-200 align-middle">
                  <MoreHorizontal className="w-4 h-4 text-gray-700" />
                </span>
                top-right
              </Step>
              <Step n={2}>
                Tap <b>“Open in External Browser”</b>
              </Step>
            </div>
          </div>
        )}

        {/* Copy link — works everywhere as a universal fallback */}
        <button
          onClick={copyLink}
          className="mt-3 w-full py-2.5 rounded-xl border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition inline-flex items-center justify-center gap-2"
        >
          {copied ? <><Check className="w-4 h-4 text-[#32764A]" />Link copied!</> : <><Copy className="w-4 h-4" />Copy link</>}
        </button>

        <button
          onClick={() => setShow(false)}
          className="mt-3 text-xs text-gray-400 hover:text-gray-600 transition"
        >
          Continue here anyway
        </button>
      </div>

      <style>{`@keyframes slideUp{from{transform:translateY(24px);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>
    </div>
  );
}

function Step({ n, children }) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="w-5 h-5 rounded-full bg-[#32764A] text-white text-[11px] font-black flex items-center justify-center flex-shrink-0 mt-0.5">{n}</span>
      <p className="text-sm text-gray-700 leading-relaxed">{children}</p>
    </div>
  );
}
